import React, {forwardRef} from 'react';
import {TableFunctions} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/Table.type';
import {RoleRow, ExpenseRow, EyeOptionMap, Company} from './BaselineTypes';
import {useIntl} from 'react-intl';
import {Table} from '@forecast-it/design-system';

type RoleTableProps = {
	editMode?: boolean;
	onDeleteRow: (row: RoleRow) => void;
	onRoleSelect: (roleId: string, row: RoleRow) => void;
	company: Company;
	ref: React.MutableRefObject<TableFunctions>;
	recalculate?: (row: RoleRow) => void;
	setTableData?: React.Dispatch<React.SetStateAction<(RoleRow | ExpenseRow)[]>>;
	tableData: RoleRow[];
	eyeOptionMap?: EyeOptionMap;
};

export const TimeEstimatesTable = forwardRef<TableFunctions, RoleTableProps>((props, ref) => {
	const {editMode, onDeleteRow, company, onRoleSelect, recalculate, tableData, setTableData, eyeOptionMap} = props;

	const {formatMessage} = useIntl();

	const getRoleName = (row: RoleRow) => {
		const role = company?.roles?.edges?.find(edge => edge?.node?.id === row.roleId);
		if (role && role.node && role.node.name) {
			return role.node.name;
		}
		return formatMessage({id: 'deliverable.select_role'});
	};

	const currency = company.currency || '';

	return (
		<Table width={'100%'} tableRef={ref} setTableData={setTableData} data={tableData}>
			{editMode ? (
				<Table.DropdownColumn header={formatMessage({id: 'deliverable.columns.role'})} label={getRoleName} width="20%">
					{(company?.roles?.edges || [])?.map(role => (
						<Table.DropdownColumn.ListItem
							onClick={row => {
								role?.node?.id && onRoleSelect(role?.node?.id, row);
							}}
						>
							{role?.node?.name}
						</Table.DropdownColumn.ListItem>
					))}
				</Table.DropdownColumn>
			) : (
				<Table.TextColumn accessorKey={'roleName'} header={formatMessage({id: 'deliverable.columns.role'})} ellipsis />
			)}
			{editMode ? (
				<Table.NumberInputColumn
					accessorKey={'hours'}
					header={formatMessage({id: 'deliverable.columns.hours'})}
					onInputChange={recalculate}
					width="20%"
				/>
			) : (
				<Table.TimeColumn
					accessorKey={'hours'}
					header={formatMessage({id: 'deliverable.columns.hours'})}
					align={Table.Align.right}
					timeUnit="hours"
					visible={eyeOptionMap ? eyeOptionMap['time_estimates.hours'] : true}
				/>
			)}
			<Table.CurrencyColumn
				accessorKey={'valueOfService'}
				header={formatMessage({id: 'deliverable.columns.value_of_service'})}
				align={Table.Align.right}
				currency={currency}
				width="12%"
				visible={eyeOptionMap ? eyeOptionMap['time_estimates.value_of_service'] : true}
			/>
			<Table.CurrencyColumn
				accessorKey={'revenue'}
				header={formatMessage({id: 'deliverable.columns.revenue'})}
				align={Table.Align.right}
				currency={currency}
				width="12%"
				visible={eyeOptionMap ? eyeOptionMap['time_estimates.revenue'] : true}
			/>
			<Table.CurrencyColumn
				accessorKey={'cost'}
				header={formatMessage({id: 'deliverable.columns.cost'})}
				align={Table.Align.right}
				currency={currency}
				width="12%"
				visible={eyeOptionMap ? eyeOptionMap['time_estimates.cost'] : true}
			/>
			<Table.CurrencyColumn
				accessorKey={'profit'}
				header={formatMessage({id: 'deliverable.columns.profit'})}
				align={Table.Align.right}
				currency={currency}
				width="12%"
				visible={eyeOptionMap ? eyeOptionMap['time_estimates.profit'] : true}
			/>
			<Table.CurrencyColumn
				accessorKey={'ratePerHour'}
				header={formatMessage({id: 'deliverable.columns.rate_per_hour'})}
				align={Table.Align.right}
				currency={currency}
				width="12%"
				visible={eyeOptionMap ? eyeOptionMap['time_estimates.rate_per_hour'] : true}
			/>
			<Table.CurrencyColumn
				accessorKey={'costPerHour'}
				header={formatMessage({id: 'deliverable.columns.cost_per_hour'})}
				align={Table.Align.right}
				currency={currency}
				width="12%"
				visible={eyeOptionMap ? eyeOptionMap['time_estimates.cost_per_hour'] : true}
			/>
			<Table.ActionColumn header={'contextmenu'} hideHeader hidden={!editMode}>
				<Table.ActionColumn.Option data-cy={'delete-baseline-role'} onClick={onDeleteRow}>
					{formatMessage({id: 'deliverable.columns.delete_time_estimate'})}
				</Table.ActionColumn.Option>
			</Table.ActionColumn>
		</Table>
	);
});
